import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText } from "@/components";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyIntake } from "@/types";

const cultureSchema = z.object({
  companyCultureDetails: z.string().nullable(),
  workplaceEnvironmentDetails: z.string().nullable(),
});

export const Culture = ({
  companyIntake,
  isEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
}) => {
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();

  const culture = companyIntake.culture;

  const form = useForm<z.infer<typeof cultureSchema>>({
    resolver: zodResolver(cultureSchema),
    defaultValues: culture,
  });

  const handleSave = (data: z.infer<typeof cultureSchema>) => {
    mixpanelUtils.trackFormSubmission("Culture section", MIXPANEL_SCREENS.COMPANY);
    updateCompanyIntake({ updateCompanyIntakeRequest: { culture: data } });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  return (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="companyCultureDetails"
              form={form}
              infoTooltip={
                "How would you describe the company's culture?\nWhat behaviors are rewarded?\nWhat excites leadership the most?"
              }
              label="Company Culture Details"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="workplaceEnvironmentDetails"
              form={form}
              infoTooltip={
                "Is the environment metrics driven or relationship driven?\nHigh volume or job shop?\nHow are mistakes handled?\nWhat frustrates leadership the most?"
              }
              label="Workplace Environment Details"
              onBlur={saveOnBlur}
            />

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <Column className="gap-4">
          <ReadOnlyRichTextField
            label="Company Culture Details"
            value={culture.companyCultureDetails}
          />
          <ReadOnlyRichTextField
            label="Workplace Environment Details"
            value={culture.workplaceEnvironmentDetails}
          />
        </Column>
      )}
    </Column>
  );
};
